import React, { useState } from 'react';
import { LoggerService } from '../../shared/services/logger.service';

interface ActiveSession {
  id: string;
  device: string;
  browser: string;
  location: string;
  ip: string;
  lastActivity: string;
  isCurrent: boolean;
}

export const UserSecurity: React.FC = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const activeSessions: ActiveSession[] = [
    {
      id: '1',
      device: 'MacBook Pro',
      browser: 'Chrome 120.0',
      location: 'Buenos Aires, Argentina',
      ip: 'IP: 192.168.1.100',
      lastActivity: 'Última actividad: 15 de ene de 2024, 10:30 a. m.',
      isCurrent: true
    },
    {
      id: '2',
      device: 'iPhone 15',
      browser: 'Safari Mobile',
      location: 'Buenos Aires, Argentina',
      ip: 'IP: 192.168.1.101',
      lastActivity: 'Última actividad: 15 de ene de 2024, 08:15 a. m.',
      isCurrent: false
    }
  ];

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordSave = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }
    
    LoggerService.info('Password change requested', 'UserSecurity');
    // TODO: Implementar cambio de contraseña
    setShowPasswordChange(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleToggle2FA = () => {
    setTwoFactorEnabled(!twoFactorEnabled);
    LoggerService.info('2FA toggle requested', 'UserSecurity', { 
      enabled: !twoFactorEnabled 
    });
  };

  const handleTerminateSession = (sessionId: string) => {
    LoggerService.info('Session termination requested', 'UserSecurity', { 
      sessionId 
    });
    // TODO: Implementar terminación de sesión
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Seguridad</h1>
        <p className="text-gray-600 mt-1">Gestiona la seguridad de tu cuenta y controla el acceso</p>
      </div>

      {/* Password Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <i className="ri-lock-line text-blue-600"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Contraseña</h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Última actualización: 15 de diciembre, 2023</p>
            <p className="text-sm text-gray-500 mt-1">Recomendamos cambiar tu contraseña cada 90 días</p>
          </div>
          
          <button
            onClick={() => setShowPasswordChange(!showPasswordChange)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Cambiar Contraseña
          </button>
        </div>

        {/* Password Change Form */}
        {showPasswordChange && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña Actual
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nueva Contraseña
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Nueva Contraseña
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPasswordChange(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handlePasswordSave}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Two Factor Authentication */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
            <i className="ri-shield-check-line text-orange-600"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Autenticación de Dos Factores</h2>
        </div>

        <div className={`p-4 rounded-lg ${twoFactorEnabled ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center space-x-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              twoFactorEnabled ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              <i className={`${twoFactorEnabled ? 'ri-check-line text-green-600' : 'ri-alert-line text-yellow-600'} text-sm`}></i>
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">
                {twoFactorEnabled ? '2FA Activado' : '2FA Desactivado'}
              </h3>
              <p className="text-sm text-gray-600">
                {twoFactorEnabled 
                  ? 'Tu cuenta está protegida con autenticación de dos factores'
                  : 'Tu cuenta no está protegida con autenticación de dos factores'
                }
              </p>
            </div>
          </div>
        </div>

        {!twoFactorEnabled && (
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-4">
              La autenticación de dos factores agrega una capa extra de seguridad a tu cuenta. 
              Recomendamos encarecidamente activar esta función.
            </p>
            <button
              onClick={handleToggle2FA}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Activar 2FA
            </button>
          </div>
        )}

        {twoFactorEnabled && (
          <div className="mt-4">
            <button
              onClick={handleToggle2FA}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Desactivar 2FA
            </button>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <i className="ri-computer-line text-purple-600"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Sesiones Activas</h2>
        </div>

        <div className="space-y-4">
          {activeSessions.map((session) => (
            <div 
              key={session.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <i className={`${
                    session.device.includes('Mac') ? 'ri-macbook-line' : 'ri-smartphone-line'
                  } text-emerald-600`}></i>
                </div>
                
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{session.device}</span>
                    {session.isCurrent && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        Actual
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{session.browser} • {session.location}</p>
                  <p className="text-xs text-gray-500">{session.ip} • {session.lastActivity}</p>
                </div>
              </div>

              {!session.isCurrent && (
                <button
                  onClick={() => handleTerminateSession(session.id)}
                  className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
                >
                  Terminar
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};